import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";
import { ArrowLeft, Bell, WifiOff } from "lucide-react";
import { cx, IconButton } from "./ui";
import { Avatar } from "./bits";
import { useNotifications } from "@/lib/queries";
import { useSession } from "@/lib/session";

export interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  badge?: number;
  end?: boolean;
}

function useOnline() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

export function AppShell({
  nav,
  notificationsTo,
  banner,
  switcher,
  bigTabs,
}: {
  nav: NavItem[];
  notificationsTo: string;
  banner?: ReactNode;
  /** Always-visible control (the parent's kid switcher, a kid's way back to a parent): above the bottom tabs on phones, in the sidebar otherwise. */
  switcher?: (variant: "bar" | "sidebar") => ReactNode;
  /** Oversized bottom tabs for small hands. */
  bigTabs?: boolean;
}) {
  const online = useOnline();
  const session = useSession();
  const { data: notes } = useNotifications();
  return (
    <div className="min-h-dvh md:pl-64">
      {!online && (
        <div className="sticky top-0 z-40 flex items-center justify-center gap-2 bg-warn px-4 pb-1.5 pt-[calc(env(safe-area-inset-top)+6px)] text-sm font-bold text-white">
          <WifiOff className="size-4" /> You're offline — changes will need a connection.
        </div>
      )}

      {/* Sidebar for tablets / desktop */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-line bg-card px-4 py-6 md:flex">
        <div className="mb-8 flex items-center gap-2 px-2">
          <img src="/favicon.svg" alt="" className="size-9" />
          <div className="leading-tight">
            <div className="font-extrabold">Meaningful</div>
            <div className="text-sm font-bold text-muted">Rewards</div>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-1" aria-label="Main">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cx(
                  "flex items-center gap-3 rounded-2xl px-3 py-2.5 font-bold transition",
                  isActive ? "bg-brand-soft text-brand-strong" : "text-muted hover:bg-sunken hover:text-ink",
                )
              }
            >
              <span className="[&>svg]:size-5">{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {!!item.badge && (
                <span className="min-w-6 rounded-full bg-danger px-1.5 text-center text-xs font-extrabold leading-6 text-white">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
          {switcher && <div className="mt-6 border-t border-line pt-4">{switcher("sidebar")}</div>}
        </nav>
        <NavLink
          to={notificationsTo}
          className="flex items-center gap-3 rounded-2xl px-3 py-2.5 font-bold text-muted hover:bg-sunken hover:text-ink"
        >
          <Avatar member={session.user} size="sm" />
          <span className="flex-1 truncate">{session.user.name}</span>
          {!!notes?.unread && <span className="size-2.5 rounded-full bg-danger" />}
        </NavLink>
      </aside>

      <main
        className={cx(
          "mx-auto w-full max-w-3xl px-4 md:px-8 md:pb-12",
          switcher ? "pb-[calc(env(safe-area-inset-bottom)+156px)]" : "pb-[calc(env(safe-area-inset-bottom)+96px)]",
        )}
      >
        {banner}
        <Outlet />
      </main>

      {/* Bottom tabs for phones */}
      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
      >
        {switcher && <div className="mx-auto max-w-lg border-b border-line">{switcher("bar")}</div>}
        <div className="mx-auto flex max-w-lg">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cx(
                  "relative flex flex-1 flex-col items-center gap-0.5 pb-2 pt-2.5 text-[11px] font-extrabold transition",
                  isActive ? "text-brand" : "text-faint",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span className={cx("rounded-full px-4 py-1 transition", bigTabs ? "[&>svg]:size-9" : "[&>svg]:size-6", isActive && "bg-brand-soft")}>
                    {item.icon}
                  </span>
                  {item.label}
                  {!!item.badge && (
                    <span className="absolute right-[calc(50%-26px)] top-1.5 min-w-5 rounded-full bg-danger px-1 text-center text-[11px] font-extrabold leading-5 text-white">
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  back,
  actions,
  bell,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  back?: string | boolean;
  actions?: ReactNode;
  bell?: string;
}) {
  const navigate = useNavigate();
  const { data: notes } = useNotifications();
  return (
    <header className="flex items-center gap-2 pb-2 pt-[calc(env(safe-area-inset-top)+16px)] md:pt-8">
      {back && (
        <IconButton
          label="Back"
          className="-ml-2"
          onClick={() => (typeof back === "string" ? navigate(back) : navigate(-1))}
        >
          <ArrowLeft className="size-6" />
        </IconButton>
      )}
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-2xl font-black tracking-tight md:text-3xl">{title}</h1>
        {subtitle && <p className="truncate text-sm font-semibold text-muted">{subtitle}</p>}
      </div>
      {actions}
      {bell && (
        <IconButton label="Notifications" onClick={() => navigate(bell)} className="relative md:hidden">
          <Bell className="size-6" />
          {!!notes?.unread && (
            <span className="absolute right-1.5 top-1.5 min-w-4 rounded-full bg-danger px-1 text-[10px] font-extrabold leading-4 text-white">
              {notes.unread > 9 ? "9+" : notes.unread}
            </span>
          )}
        </IconButton>
      )}
    </header>
  );
}
